import {
    createMetaDescription,
    createMetaTitle,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import type { MetaDescriptor } from 'react-router';
import {
    imageLabelCanonicalQuery,
    type ProjectImageLabelView,
} from '../config/project-browse-views';
import type { createImageLabelBrowseLoader } from './image-label-browse-loader.server';

type ImageLabelBrowseLoader = ReturnType<typeof createImageLabelBrowseLoader>;

/**
 * SEO tags for a Spaces / Styles browse.
 *
 * These pages are a wall of labelled photography, so the body text a crawler
 * sees is each image's alt — the AI's own description of the photo. The title
 * and description name the axis being browsed, and the active pill within it,
 * so the page reads as its own destination rather than a near-duplicate of
 * /projects. The canonical keeps the facet (`?roomTypePrimary=bathroom`), so each
 * room or style is its own page and every filtered view of it folds into it.
 */
export const createImageLabelBrowseMeta = (view: ProjectImageLabelView) =>
    mergeMeta<ImageLabelBrowseLoader>(
        () => [],
        (props) => {
            const { data } = props;
            const noun = view === 'styles' ? 'style' : 'space';
            const label = data?.selectedLabel ?? data?.rootLabel;

            const title = createMetaTitle(
                props,
                [
                    data?.selectedLabel
                        ? `${data.selectedLabel} project photos`
                        : `Browse project photos by ${noun}`,
                ],
                true,
                data?.page,
            );

            const description = createMetaDescription([
                `Browse project photography by ${noun}${
                    label ? `, starting with ${label}` : ''
                }. Every photo is labelled from the image itself, so you can find the rooms, styles and finishes you want and go straight to the professionals behind them.`,
            ]);

            const metaDescriptors: MetaDescriptor[] = [
                { title },
                { name: 'description', content: description },
                ...createOGTags({ meta: props, title, description }),
                // Indexed by the view's own facet alone: every other filter
                // narrows the same wall, so it folds into the facet page. A
                // value no pill offers (a typo, a stale link) folds into the
                // root, so it never becomes an indexable empty page.
                ...getCanonicalHreflang(props, {
                    canonicalQuery: data?.selectedLabel
                        ? imageLabelCanonicalQuery(
                              view,
                              new URLSearchParams(props.location.search),
                          )
                        : '',
                }),
            ];

            return metaDescriptors;
        },
    );
